import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BulkAccessColumn } from '../bulk-access.models';

export interface BulkAccessAddSubmit<T> {
	rows: T[];
	/** Every candidate matching the current search is selected (drives the confirm copy). */
	allMatching: boolean;
}

/**
 * DOVE-2626 — Add dialog: global search over the candidates, per-row checkboxes and a header
 * checkbox that reaches every candidate matching the search (not just the visible page).
 * The parent decides whether the batch needs the count confirmation before granting.
 */
@Component({
	selector: 'app-bulk-access-add-dialog',
	templateUrl: './bulk-access-add-dialog.component.html'
})
export class BulkAccessAddDialogComponent<T extends Record<string, any>> implements OnChanges {
	@Input() visible = false;
	@Input() header = '';
	@Input() rows: T[] = [];
	@Input() columns: BulkAccessColumn[] = [];
	@Input() idField = 'id';
	@Input() loading = false;
	@Input() saving = false;
	@Input() emptyMessage = 'Nothing available to add.';

	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() submitted = new EventEmitter<BulkAccessAddSubmit<T>>();

	search = '';
	filteredRows: T[] = [];
	selection: T[] = [];

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible']?.currentValue && !changes['visible'].previousValue) {
			// Every open starts clean: no carried-over search or selection.
			this.search = '';
			this.selection = [];
		}
		if (changes['rows'] || changes['visible']) {
			this.applySearch();
		}
	}

	onSearch(value: string): void {
		this.search = value;
		this.applySearch();
		// The selection was made against the previous match set.
		this.selection = [];
	}

	get allMatchingSelected(): boolean {
		return this.filteredRows.length > 0 && this.selection.length === this.filteredRows.length;
	}

	close(): void {
		this.visibleChange.emit(false);
	}

	submit(): void {
		if (!this.selection.length) return;
		this.submitted.emit({ rows: [...this.selection], allMatching: this.allMatchingSelected });
	}

	private applySearch(): void {
		const term = this.search.trim().toLowerCase();
		this.filteredRows = term
			? this.rows.filter((row) =>
					this.columns.some((col) =>
						String(row[col.field] ?? '')
							.toLowerCase()
							.includes(term)
					)
				)
			: [...this.rows];
	}
}
